import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { ArrowLeft, Loader2, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { authApi } from '@services/api'

interface ForgotPasswordFormData {
  email: string
}

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [networkError, setNetworkError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>()

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    setNetworkError(null)
    try {
      await authApi.forgotPassword(data.email)
      toast.success(t('auth.forgotPassword.success'))
      setIsSubmitted(true)
    } catch {
      const msg = t('auth.forgotPassword.networkError')
      setNetworkError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className="animate-in text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          E-Mail gesendet
        </h2>
        <p className="text-gray-600 mb-8">
          {t('auth.forgotPassword.success')}
        </p>
        <Link data-testid="auth-forgot-password-login-link-1" to="/login" className="btn-primary">
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </div>
    )
  }

  return (
    <div className="animate-in">
      {/* Back Link */}
      <Link
        data-testid="auth-forgot-password-login-link-2"
        to="/login"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
      >
        <ArrowLeft size={20} />
        {t('auth.forgotPassword.backToLogin')}
      </Link>

      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('auth.forgotPassword.title')}
        </h2>
        <p className="text-gray-600 mt-2">
          {t('auth.forgotPassword.subtitle')}
        </p>
      </div>

      {/* Form */}
      <form data-testid="auth-forgot-password-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email */}
        <div>
          <label htmlFor="email" className="label">
            {t('auth.forgotPassword.email')}
          </label>
          <input
            data-testid="auth-forgot-password-email-input"
            id="email"
            type="email"
            autoComplete="email"
            className={errors.email ? 'input-error' : 'input'}
            {...register('email', {
              required: t('validation.required'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('validation.email'),
              },
            })}
          />
          {errors.email && (
            <p className="error-text">{errors.email.message}</p>
          )}
        </div>

        {/* Network Error */}
        {networkError && (
          <p className="text-sm text-red-600" role="alert">{networkError}</p>
        )}

        {/* Submit Button */}
        <button
          data-testid="auth-forgot-password-submit-btn"
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3"
        >
          {isLoading ? (
            <>
              <Loader2 size={20} className="animate-spin mr-2" />
              {t('common.loading')}
            </>
          ) : (
            t('auth.forgotPassword.submit')
          )}
        </button>
      </form>
    </div>
  )
}
